/**
 * SetupWelcome — pre-setup Home screen.
 *
 * Renders when `checklist.isSetupComplete === false` (i.e. at least one of the
 * 4 onboarding steps is not yet done). The screen is a focused "help with
 * setup" view:
 *   - Welcome heading
 *   - Next Action banner (the single most important thing to do)
 *   - Onboarding checklist with CTA buttons per step
 *   - "Run setup wizard" primary button
 *
 * The full operator dashboard (status cards, analytics, publish flow) is
 * hidden until setup is complete. This is intentional — pre-setup merchants
 * should see only what helps them finish setup.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Button, Box, Typography } from '@material-ui/core';
import { Build } from '@material-ui/icons';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import OnboardingChecklist from '../OnboardingChecklist';
import AgentMcpSetupCard from '../AgentMcpSetupCard';
import { deriveChecklistSteps } from '../../deriveChecklistSteps';
import { resolveActionRoute } from '../../resolveActionRoute';
import { ActionRoute } from '../../operatorState';
import styles from './SetupWelcome.module.scss';

export default function SetupWelcome({
  operatorState,
  shopId,
  isLoading,
  checklist: providedChecklist,
  onPublish,
}) {
  const localizeMessage = useLocalizedMessage();
  const history = useHistory();

  const checklist = providedChecklist || deriveChecklistSteps(operatorState);
  const { steps, isSetupComplete, activeStepIndex } = checklist;

  const handleRunWizard = () => {
    if (shopId) history.push(`/app/${shopId}/setup`);
  };

  const handleActiveStep = () => {
    const activeStep = steps[activeStepIndex];
    if (!activeStep) return;
    if (activeStep.route === ActionRoute.HOME_ACTIVATE) {
      if (onPublish) onPublish();
      return;
    }
    const path = resolveActionRoute(activeStep.route, shopId);
    if (path) history.push(path);
  };

  return (
    <div
      className={styles.welcome}
      data-testid="setup-welcome"
      data-setup-complete={String(isSetupComplete)}
    >
      <Box className={styles.hero} data-testid="setup-welcome-hero">
        <Build className={styles.heroIcon} fontSize="large" />
        <Typography variant="h4" component="h2" className={styles.heroTitle} data-testid="setup-welcome-title">
          {localizeMessage('operator.welcome.title')}
        </Typography>
        <Typography variant="body1" className={styles.heroSubtitle} data-testid="setup-welcome-subtitle">
          {localizeMessage('operator.welcome.subtitle')}
        </Typography>
        <Box className={styles.heroActions} data-testid="setup-welcome-actions">
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleActiveStep}
            disabled={isLoading || activeStepIndex < 0}
            data-testid="setup-welcome-resume-btn"
          >
            {localizeMessage('operator.welcome.cta_resume')}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleRunWizard}
            disabled={isLoading || !shopId}
            data-testid="setup-welcome-wizard-btn"
          >
            {localizeMessage('operator.welcome.cta_wizard')}
          </Button>
        </Box>
      </Box>

      <AgentMcpSetupCard variant="setup" />

      <OnboardingChecklist
        checklist={checklist}
        shopId={shopId}
        localizeMessage={localizeMessage}
        isLoading={isLoading}
        onActivate={onPublish}
      />
    </div>
  );
}

SetupWelcome.propTypes = {
  operatorState: PropTypes.shape({}).isRequired,
  shopId: PropTypes.string,
  isLoading: PropTypes.bool,
  checklist: PropTypes.shape({
    steps: PropTypes.array.isRequired,
    completedCount: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    isSetupComplete: PropTypes.bool.isRequired,
    activeStepIndex: PropTypes.number.isRequired,
  }),
  onPublish: PropTypes.func,
};

SetupWelcome.defaultProps = {
  shopId: null,
  isLoading: false,
  checklist: null,
  onPublish: null,
};
